import { PageHeader, FilterBar } from "@/components/ui/page";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, ShoppingCart, FileText, Receipt, RotateCcw, Truck, ChevronDown, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { usePermission } from "@/hooks/usePermission";
import { salesAPI, purchasesAPI, returnsAPI, quotationsAPI } from "@/lib/api";
import { Price } from "@/components/ui/price";
import { useCurrency } from "@/hooks/useCurrency";
import { cn } from "@/lib/utils";
import type { HubFilters } from "@/features/sales/hubFilters";
import SalesPage from "./SalesPage";
import QuotationsPage from "@/features/quotations/pages/QuotationsPage";
import InvoicesPage from "@/features/invoices/pages/InvoicesPage";
import ReturnsPage from "@/features/returns/pages/ReturnsPage";
import PurchasesPage from "@/features/purchases/pages/PurchasesPage";

type DocType = "sales" | "invoices" | "quotations" | "returns" | "purchases";

/** Accent color per document type (chip dot, KPI border, icon tint). */
const TYPE_STYLES: Record<DocType, { dot: string; text: string; bg: string; border: string }> = {
	sales: { dot: "bg-info", text: "text-info", bg: "bg-info-soft", border: "border-s-info" },
	invoices: { dot: "bg-success", text: "text-success", bg: "bg-success-soft", border: "border-s-success" },
	quotations: { dot: "bg-violet", text: "text-violet", bg: "bg-violet-soft", border: "border-s-violet" },
	returns: { dot: "bg-warning", text: "text-warning", bg: "bg-warning-soft", border: "border-s-warning" },
	purchases: { dot: "bg-orange", text: "text-orange", bg: "bg-orange-soft", border: "border-s-orange" },
};

const TYPE_ICONS: Record<DocType, React.ComponentType<{ className?: string }>> = {
	sales: ShoppingCart,
	invoices: Receipt,
	quotations: FileText,
	returns: RotateCcw,
	purchases: Truck,
};

interface KpiData {
	salesCount?: number;
	salesRevenue?: number;
	purchasesCount?: number;
	purchasesSpent?: number;
	returnsCount?: number;
	returnsAmount?: number;
	quotationsCount?: number;
	unpaidInvoices?: number;
}

/**
 * All sales documents in one page: sales, invoices, quotations,
 * returns and purchases as colored type views over one screen,
 * with a single create menu.
 */
const SalesHubPage: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const currency = useCurrency();
	const [searchParams, setSearchParams] = useSearchParams();

	const canSales = usePermission("sales.read");
	const canReturns = usePermission("returns.read");
	const canPurchases = usePermission("purchases.read");
	const canCreateSale = usePermission("sales.create");
	const canCreateReturn = usePermission("returns.create");
	const canCreatePurchase = usePermission("purchases.create");

	const availableTypes: DocType[] = [
		...(canSales ? (["sales", "invoices", "quotations"] as DocType[]) : []),
		...(canReturns ? (["returns"] as DocType[]) : []),
		...(canPurchases ? (["purchases"] as DocType[]) : []),
	];

	const paramType = searchParams.get("type") as DocType | null;
	const activeType: DocType =
		paramType && availableTypes.includes(paramType) ? paramType : (availableTypes[0] ?? "sales");

	const [kpi, setKpi] = useState<KpiData>({});
	const [addMenuOpen, setAddMenuOpen] = useState(false);
	const addMenuRef = useRef<HTMLDivElement | null>(null);

	// One toolbar owns the filters for every embedded list
	const [searchTerm, setSearchTerm] = useState("");
	const [status, setStatus] = useState("");
	const [paymentStatus, setPaymentStatus] = useState("");
	const [dateFrom, setDateFrom] = useState("");
	const [dateTo, setDateTo] = useState("");

	const filters: HubFilters = useMemo(
		() => ({ search: searchTerm, status, paymentStatus, dateFrom, dateTo }),
		[searchTerm, status, paymentStatus, dateFrom, dateTo],
	);

	const hasFilters = Boolean(searchTerm || status || paymentStatus || dateFrom || dateTo);

	const clearFilters = () => {
		setSearchTerm("");
		setStatus("");
		setPaymentStatus("");
		setDateFrom("");
		setDateTo("");
	};

	/** Status options per document type (same values the old filter cards sent). */
	const statusOptions: Record<DocType, { value: string; label: string }[]> = {
		sales: ["pending", "confirmed", "cancelled", "refunded"].map(v => ({
			value: v,
			label: t(`sales.filters.${v}`),
		})),
		invoices: ["paid", "partial", "pending", "overdue", "cancelled"].map(v => ({
			value: v,
			label: t(`invoices.status.${v}`),
		})),
		quotations: ["draft", "sent", "accepted", "rejected", "expired", "cancelled", "converted"].map(v => ({
			value: v,
			label: t(`quotations.status.${v}`),
		})),
		returns: ["pending", "approved", "rejected", "completed"].map(v => ({
			value: v,
			label: t(`returns.status.${v}`),
		})),
		purchases: ["pending", "approved", "received", "cancelled"].map(v => ({
			value: v,
			label: t(`purchases.statuses.${v}`),
		})),
	};

	const allStatusesLabel: Record<DocType, string> = {
		sales: t("sales.filters.allStatuses"),
		invoices: t("invoices.allStatuses"),
		quotations: t("quotations.allStatuses"),
		returns: t("returns.allStatuses"),
		purchases: t("purchases.allStatuses"),
	};

	useEffect(() => {
		const today = new Date().toISOString().split("T")[0];
		if (canSales) {
			salesAPI
				.getStatistics(undefined, today, today)
				.then((s: any) =>
					setKpi(prev => ({
						...prev,
						salesCount: s?.totalSales ?? 0,
						salesRevenue: Number(s?.totalRevenue) || 0,
					})),
				)
				.catch(() => undefined);
			salesAPI
				.getAll({ page: 1, limit: 1, paymentStatus: "pending" })
				.then((res: any) => setKpi(prev => ({ ...prev, unpaidInvoices: res?.total ?? 0 })))
				.catch(() => undefined);
			quotationsAPI
				.getAll(1, 1)
				.then((res: any) => setKpi(prev => ({ ...prev, quotationsCount: res?.total ?? 0 })))
				.catch(() => undefined);
		}
		if (canPurchases) {
			purchasesAPI
				.getStatistics(undefined, today, today)
				.then((s: any) =>
					setKpi(prev => ({
						...prev,
						purchasesCount: s?.totalPurchases ?? 0,
						purchasesSpent: Number(s?.totalSpent) || 0,
					})),
				)
				.catch(() => undefined);
		}
		if (canReturns) {
			returnsAPI
				.getStatistics()
				.then((s: any) =>
					setKpi(prev => ({
						...prev,
						returnsCount: s?.totalReturns ?? 0,
						returnsAmount: Number(s?.totalAmount) || 0,
					})),
				)
				.catch(() => undefined);
		}
	}, [canSales, canPurchases, canReturns]);

	// Close the add menu on outside click
	useEffect(() => {
		if (!addMenuOpen) return undefined;
		const close = (event: MouseEvent) => {
			if (addMenuRef.current && !addMenuRef.current.contains(event.target as Node)) {
				setAddMenuOpen(false);
			}
		};
		document.addEventListener("mousedown", close);
		return () => document.removeEventListener("mousedown", close);
	}, [addMenuOpen]);

	const switchType = (type: DocType) => {
		clearFilters();
		setSearchParams(type === "sales" ? {} : { type }, { replace: true });
	};

	const typeLabel = (type: DocType) =>
		t(
			type === "sales"
				? "navigation.sales"
				: type === "invoices"
					? "navigation.invoices"
					: type === "quotations"
						? "navigation.quotations"
						: type === "returns"
							? "navigation.returns"
							: "navigation.purchases",
		);

	const addOptions = [
		...(canCreateSale
			? [
					{ type: "sales" as DocType, label: t("navigation.sales"), to: "/sales/new" },
					{ type: "quotations" as DocType, label: t("navigation.quotations"), to: "/quotations/new" },
				]
			: []),
		...(canCreateReturn
			? [{ type: "returns" as DocType, label: t("navigation.returns"), to: "/returns/new" }]
			: []),
		...(canCreatePurchase
			? [{ type: "purchases" as DocType, label: t("navigation.purchases"), to: "/purchases/new" }]
			: []),
	];

	const kpiCards: { type: DocType; value: React.ReactNode; label: string; show: boolean }[] = [
		{
			type: "sales",
			value: <Price value={kpi.salesRevenue ?? 0} currency={currency} />,
			label: `${t("salesHub.kpi.todaySales")} · ${kpi.salesCount ?? 0}`,
			show: canSales,
		},
		{
			type: "purchases",
			value: <Price value={kpi.purchasesSpent ?? 0} currency={currency} />,
			label: `${t("salesHub.kpi.todayPurchases")} · ${kpi.purchasesCount ?? 0}`,
			show: canPurchases,
		},
		{
			type: "returns",
			value: <Price value={kpi.returnsAmount ?? 0} currency={currency} />,
			label: `${t("navigation.returns")} · ${kpi.returnsCount ?? 0}`,
			show: canReturns,
		},
		{
			type: "quotations",
			value: kpi.quotationsCount ?? 0,
			label: t("navigation.quotations"),
			show: canSales,
		},
		{
			type: "invoices",
			value: kpi.unpaidInvoices ?? 0,
			label: t("salesHub.kpi.unpaidInvoices"),
			show: canSales,
		},
	];

	if (availableTypes.length === 0) return null;

	return (
		<div className="space-y-4 p-4 md:p-6">
			<PageHeader
				title={t("navigation.sales")}
				description={t("design.salesDescription")}
				actions={
					<>
						{addOptions.length > 0 && (
							<div className="relative shrink-0" ref={addMenuRef}>
								<Button
									className="flex w-full items-center gap-2 sm:w-auto"
									onClick={() => setAddMenuOpen(open => !open)}
									aria-expanded={addMenuOpen}
									aria-haspopup="menu"
								>
									<Plus className="h-4 w-4" />
									{t("salesHub.addDocument")}
									<ChevronDown className="h-4 w-4" />
								</Button>
								{addMenuOpen && (
									<div className="absolute end-0 top-full z-20 mt-2 w-60 overflow-hidden rounded-xl border bg-card shadow-lg">
										{addOptions.map(option => {
											const Icon = TYPE_ICONS[option.type];
											const style = TYPE_STYLES[option.type];
											return (
												<button
													key={option.to}
													type="button"
													className="flex w-full items-center gap-3 border-b px-4 py-3 text-sm transition-colors last:border-b-0 hover:bg-muted/50"
													onClick={() => {
														setAddMenuOpen(false);
														navigate(option.to);
													}}
												>
													<span
														className={cn(
															"flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
															style.bg,
															style.text,
														)}
													>
														<Icon className="h-4 w-4" />
													</span>
													<span>{option.label}</span>
												</button>
											);
										})}
									</div>
								)}
							</div>
						)}
					</>
				}
			/>
			{/* KPI row + create menu */}
			<div className="flex flex-col gap-4">
				<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
					<div className="grid flex-1 grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
						{kpiCards
							.filter(card => card.show)
							.map(card => {
								const Icon = TYPE_ICONS[card.type];
								const style = TYPE_STYLES[card.type];
								return (
									<div
										key={card.type}
										className={cn(
											"flex items-center gap-3 rounded-xl border-s-[3px] bg-card px-4 py-4",
											style.border,
										)}
									>
										<span
											className={cn(
												"flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
												style.bg,
												style.text,
											)}
										>
											<Icon className="h-4 w-4" />
										</span>
										<div className="min-w-0">
											<div className="truncate text-base font-bold leading-none">
												{card.value}
											</div>
											<div className="mt-1 truncate text-xs text-muted-foreground">
												{card.label}
											</div>
										</div>
									</div>
								);
							})}
					</div>
				</div>

				{/* Unified toolbar: type chips + search + status + dates */}
				<FilterBar className="flex flex-wrap items-center gap-2 rounded-xl border bg-card p-3">
					{availableTypes.map(type => {
						const style = TYPE_STYLES[type];
						const isActive = activeType === type;
						return (
							<button
								key={type}
								type="button"
								aria-pressed={isActive}
								onClick={() => switchType(type)}
								className={cn(
									"flex min-h-11 shrink-0 items-center gap-2 rounded-[var(--radius-button)] border border-transparent px-4 py-2 text-sm transition-colors",
									isActive
										? cn("font-semibold", style.bg, style.text, "border-current")
										: "bg-card text-muted-foreground hover:border-primary/50",
								)}
							>
								<span
									className={cn(
										"h-2 w-2 rounded-full",
										style.dot,
									)}
								/>
								{typeLabel(type)}
							</button>
						);
					})}
					<span className="mx-1 hidden h-6 w-px bg-border sm:block" />
					{activeType !== "quotations" && (
						<div className="relative min-w-40 flex-1">
							<Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-muted-foreground" />
							<Input
								aria-label={t("common.from")}
								placeholder={t("common.search")}
								value={searchTerm}
								onChange={e => setSearchTerm(e.target.value)}
								className="h-10 ps-10"
							/>
						</div>
					)}
					<select
						value={status}
						onChange={e => setStatus(e.target.value)}
						aria-label={t("common.status")}
						className="flex h-10 w-auto min-w-28 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
					>
						<option value="">{allStatusesLabel[activeType]}</option>
						{statusOptions[activeType].map(option => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</select>
					{activeType === "sales" && (
						<select
							value={paymentStatus}
							onChange={e => setPaymentStatus(e.target.value)}
							aria-label={t("sales.filters.allPaymentStatuses")}
							className="flex h-10 w-auto min-w-28 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
						>
							<option value="">{t("sales.filters.allPaymentStatuses")}</option>
							{["pending", "paid", "partial", "refunded"].map(v => (
								<option key={v} value={v}>
									{t(`sales.filters.${v}`)}
								</option>
							))}
						</select>
					)}
					{activeType !== "quotations" && (
						<>
							<label className="flex items-center gap-1.5 text-xs text-muted-foreground">
								{t("common.from")}
								<Input
									type="date"
									value={dateFrom}
									onChange={e => setDateFrom(e.target.value)}
									aria-label={t("common.dateFrom")}
									className="h-10 w-38 text-sm text-foreground sm:w-40"
								/>
							</label>
							<label className="flex items-center gap-1.5 text-xs text-muted-foreground">
								{t("common.to")}
								<Input
									type="date"
									value={dateTo}
									onChange={e => setDateTo(e.target.value)}
									aria-label={t("common.dateTo")}
									className="h-10 w-38 text-sm text-foreground sm:w-40"
								/>
							</label>
						</>
					)}
					{hasFilters && (
						<Button variant="link" className="text-destructive" onClick={clearFilters}>
							{t("products.filters.clearFilters")}
						</Button>
					)}
				</FilterBar>
			</div>

			{/* Active document list (filters come from the toolbar above) */}
			{activeType === "sales" && <SalesPage filters={filters} />}
			{activeType === "invoices" && <InvoicesPage filters={filters} />}
			{activeType === "quotations" && <QuotationsPage filters={filters} />}
			{activeType === "returns" && <ReturnsPage filters={filters} />}
			{activeType === "purchases" && <PurchasesPage filters={filters} />}
		</div>
	);
};

export default SalesHubPage;
